import { ALFABETO as ALFABETO_PANTALLA, LARGO_CODIGO as LARGO_PANTALLA, formatearCodigo } from './codigoDeTerminal';
import { leerEnlaceDeTerminal } from './enlaceDeTerminal';

// El código de activación de la tarjeta de la caja (DISEÑO-F1c D4, D15).
// Diez símbolos de un alfabeto sin 0, O, 1, I, L ni U; se muestra XXXXX-XXXXX.
export const ALFABETO_ACTIVACION = '23456789ABCDEFGHJKMNPQRSTVWXYZ';
export const LARGO_ACTIVACION = 10;
const PROHIBIDAS = '0O1ILU';

// → { codigo (sin guion, ≤ 10), mostrado (con guion), prohibidas: [letras] }
export function normalizarActivacion(texto) {
  const limpio = String(texto || '').replace(/[\s-]+/g, '').toUpperCase();
  const prohibidas = [...new Set([...limpio].filter((c) => PROHIBIDAS.includes(c)))];
  const codigo = [...limpio].filter((c) => ALFABETO_ACTIVACION.includes(c)).join('').slice(0, LARGO_ACTIVACION);
  const mostrado = codigo.length > 5 ? `${codigo.slice(0, 5)}-${codigo.slice(5)}` : codigo;
  return { codigo, mostrado, prohibidas };
}

export function activacionCompleta(codigo) {
  return String(codigo || '').length === LARGO_ACTIVACION;
}

// El QR de la tarjeta: TEGOACT1/<serie>/<codigo>. No es una dirección.
export function leerTarjeta(texto) {
  const m = /^TEGOACT1\/([A-Z0-9-]{8,32})\/([A-Z0-9-]{10,11})$/.exec(String(texto || '').trim().toUpperCase());
  if (!m) return null;
  return { serie: m[1], codigo: normalizarActivacion(m[2]).codigo };
}

export function motivoDeProhibidas(prohibidas) {
  return prohibidas.length
    ? `Los códigos no llevan 0, O, 1, I, L ni U (se quitó: ${prohibidas.join(', ')}). Revisa la tarjeta.`
    : null;
}

// =====================================================================
// EL CAMPO «CÓDIGO DE LA TARJETA», tal como lo usa una persona con prisa
// (TERMINAL-CODIGO-TARJETA, 11 oct 2026). El dueño, en su Android con
// Gboard, escribió en este campo el código de la PANTALLA (6MY-WS2) y la
// máscara lo dejó en «6MYWS-2» sin decir nada. Desde aquí:
//   · se acepta con o sin guion, con espacios, en cualquier caja, y pegado
//     con texto alrededor («Tu código es abcde-fghjk.»);
//   · 0, O, 1, I, L y U NO se borran en silencio: se quedan donde están y
//     se avisa en vivo qué carácter revisar (el alfabeto no tiene ninguno
//     de ellos, así que no hay un «equivalente» que poner en su lugar);
//   · un código con forma de código de pantalla (3-3, o 6 de golpe, o el
//     mismo que ya se escribió en el paso 2, o el QR de la terminal) se
//     reconoce y se dice cuál va aquí.
// Lo que se manda al servidor es lo que `normalizar_codigo` de
// `backend/app/terminal/fabrica.py` dejaría: sin espacios ni guiones, en
// mayúsculas (lo comprueba `terminalCodigoTarjeta.test.js` leyendo los dos).
// =====================================================================

const NO_SIGNIFICA = /[\s-]+/g;     // lo mismo que quita normalizar_codigo
const SOLO_CODIGO = /^[A-Za-z0-9\s-]*$/;
// Un código 5-5 dentro de un texto más largo: cinco, un guion o un espacio,
// cinco, sin pegarse a otras letras. Con lookahead para ver también los
// que se solapan («entra desde ABCDE-FGHJK» trae dos, no uno).
const CANDIDATO = /(?<![A-Za-z0-9])(?=([A-Za-z0-9]{5}[ \t-][A-Za-z0-9]{5})(?![A-Za-z0-9]))/g;
// El guion (o espacio) TECLEADO detrás de los 3 primeros: la forma del
// código de pantalla. Se conserva mientras se escribe (P0-1 de la auditoría).
const TRES_Y_GUION = /^\s*([A-Za-z0-9]{3})[\s-]+([A-Za-z0-9]{0,3})\s*$/;

/** Lo que haría `normalizar_codigo` del servidor: sin espacios ni guiones, en mayúsculas. */
export function comoElServidor(texto) {
  return String(texto ?? '').replace(NO_SIGNIFICA, '').toUpperCase();
}

/** `ABCDE23456` → `ABCDE-23456`; con menos de 6, tal cual. */
export function formatearTarjeta(limpio) {
  const c = String(limpio ?? '');
  return c.length > 5 ? `${c.slice(0, 5)}-${c.slice(5)}` : c;
}

const enAlfabeto = (c, alfabeto) => [...c].every((x) => alfabeto.includes(x));

/**
 * Los códigos con forma 5-5 (con guion o espacio) que trae un texto pegado,
 * sin repetir. No se elige entre ellos: si no hay exactamente uno, quien
 * llama pide que se pegue solo el código (P1-1 de la auditoría: con «entra
 * desde la app con ABCDE-FGHJK» se mandaba ENTRADESDE).
 */
export function candidatosPegados(texto) {
  return [...new Set([...String(texto ?? '').matchAll(CANDIDATO)].map((m) => comoElServidor(m[1])))];
}

// El código de PANTALLA que trae un QR o una dirección de la terminal.
function pantallaDeEnlace(texto) {
  const crudo = String(texto ?? '').trim();
  if (!/[/:]/.test(crudo)) return null;
  let camino = crudo;
  try { camino = new URL(crudo).pathname; } catch { camino = crudo.replace(/^[^/]*(?=\/t\/)/i, ''); }
  const leido = leerEnlaceDeTerminal(camino);
  return leido?.codigo ? { codigo: leido.codigo, tipo: leido.tipo } : null;
}

/**
 * Lee lo que hay en el campo de la tarjeta.
 *   texto            lo que trae el campo (tecleado, pegado o dictado)
 *   codigoDePantalla el código corto de la terminal elegida, si se sabe
 *   deUnGolpe        verdadero si llegó entero (pegado, reemplazo del
 *                    teclado, escáner)
 *   serie            la serie de la terminal; un TEGOACT1 de otra se rechaza
 * → { codigo, mostrado, prohibidas, pantalla, completo, enlace, pegado,
 *     candidatos, otraSerie }
 *   `pantalla`: el código corto reconocido, o null. `enlace`: el tipo de
 *   QR de la terminal si vino de uno. `pegado`: 'VARIOS' o 'NINGUNO' si de
 *   lo pegado no sale exactamente un código. `otraSerie`: la serie de una
 *   tarjeta TEGOACT1 que no es la de esta terminal.
 */
export function leerCodigoDeTarjeta(texto, { codigoDePantalla = null, deUnGolpe = false, serie = null } = {}) {
  // Ancho completo (ＡＢＣ) y compatibles a su forma de siempre.
  const crudo = String(texto ?? '').normalize('NFKC');
  const tarjeta = leerTarjeta(crudo);
  if (tarjeta) {
    if (serie && tarjeta.serie !== String(serie).trim().toUpperCase()) return { ...resultado(''), otraSerie: tarjeta.serie };
    return resultado(tarjeta.codigo);
  }
  const enlace = pantallaDeEnlace(crudo);
  if (enlace) return { ...resultado(''), mostrado: formatearCodigo(enlace.codigo), pantalla: enlace.codigo, enlace: enlace.tipo };

  const soloCodigo = SOLO_CODIGO.test(crudo);
  let limpio = comoElServidor(crudo);
  if (!soloCodigo || (deUnGolpe && limpio.length > LARGO_ACTIVACION)) {
    const candidatos = candidatosPegados(crudo);
    if (candidatos.length !== 1) {
      return { ...resultado(''), pegado: candidatos.length ? 'VARIOS' : 'NINGUNO', candidatos };
    }
    [limpio] = candidatos;
  }
  limpio = limpio.slice(0, LARGO_ACTIVACION);

  const corto = limpio.length === LARGO_PANTALLA && enAlfabeto(limpio, ALFABETO_PANTALLA);
  const pantallaConocida = codigoDePantalla && limpio === comoElServidor(codigoDePantalla);
  const tres = soloCodigo ? TRES_Y_GUION.exec(crudo) : null;
  if (corto && (pantallaConocida || tres || deUnGolpe)) {
    return { ...resultado(''), mostrado: formatearCodigo(limpio), pantalla: limpio };
  }
  // A medias con el guion tecleado tras 3 («6MY-», «6MY-WS»): el guion se
  // queda; con el sexto carácter es la forma de la pantalla y se avisa.
  if (tres) return { ...resultado(limpio), mostrado: `${tres[1]}-${tres[2]}`.toUpperCase() };
  return resultado(limpio);
}

function resultado(limpio) {
  const prohibidas = [...new Set([...limpio].filter((c) => !ALFABETO_ACTIVACION.includes(c)))];
  return {
    codigo: limpio,
    mostrado: formatearTarjeta(limpio),
    prohibidas,
    pantalla: null,
    enlace: null,
    pegado: null,
    candidatos: [],
    otraSerie: null,
    completo: limpio.length === LARGO_ACTIVACION && prohibidas.length === 0,
  };
}

/** Qué decir cuando de lo pegado no sale exactamente un código, o null. */
export function avisoDePegado(r) {
  if (r?.pegado === 'VARIOS') {
    return `Lo que pegaste trae más de un código posible (${r.candidatos.map(formatearTarjeta).join(', ')}). `
      + 'Pega solo el código de 10 caracteres de la tarjeta.';
  }
  if (r?.pegado === 'NINGUNO') {
    return 'En lo que pegaste no hay un código con la forma XXXXX-XXXXX. Pega solo el código de 10 caracteres de la tarjeta.';
  }
  return null;
}

/** Qué decir de los caracteres que la tarjeta no usa, o null. */
export function avisoDeProhibidas(prohibidas) {
  if (!prohibidas?.length) return null;
  const lista = prohibidas.map((c) => `«${c}»`).join(', ');
  return `La tarjeta no usa 0, O, 1, I, L ni U; revisa ${prohibidas.length === 1 ? 'ese carácter' : 'esos caracteres'}: ${lista}.`;
}

export const AVISO_PANTALLA = 'Ese es el código de la pantalla de la terminal; aquí va el de 10 caracteres '
  + 'de la tarjeta de la caja o el que te llegó por correo o por el bot.';

/**
 * Dónde queda el cursor después de reformatear: detrás del mismo número de
 * caracteres que contaban antes del cursor (el guion no cuenta).
 */
export function cursorTrasFormatear(mostrado, significativos) {
  if (significativos <= 0) return 0;
  let vistos = 0;
  for (let i = 0; i < mostrado.length; i += 1) {
    if (/[A-Za-z0-9]/.test(mostrado[i])) vistos += 1;
    if (vistos === significativos) return i + 1;
  }
  return mostrado.length;
}

export const significativosAntes = (texto, pos) => String(texto ?? '').slice(0, pos ?? 0).replace(/[^A-Za-z0-9]/g, '').length;
